/**
 * Product Cards Quick View Component
 * Handles the quick view modal functionality for the Product Cards section
 */

class ProductCardsQuickView extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('[ref="dialog"]');
    this.closeButton = this.querySelector('[ref="closeButton"]');
    this.content = this.querySelector('[ref="content"]');
    this.boundHandleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundHandleKeydown);
  }

  setupEventListeners() {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Click outside to close
    if (this.dialog) {
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) {
          this.close();
        }
      });
    }

    // Quick view triggers - delegate from document to handle dynamically added elements
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quick-view-trigger]');
      if (trigger) {
        e.preventDefault();
        const productHandle = trigger.dataset.productHandle;
        const productUrl = trigger.dataset.productUrl;
        const productAvailable = trigger.dataset.productAvailable !== 'false';
        const productShortDescription = trigger.dataset.productShortDescription || '';
        const productBadge = trigger.dataset.productBadge || '';
        const badgeBgColor = trigger.dataset.badgeBgColor;
        const badgeTextColor = trigger.dataset.badgeTextColor;
        if (productHandle || productUrl) {
          const hasUpsell = trigger.dataset.hasUpsell === 'true';
          this.open(
            productHandle,
            productUrl,
            productAvailable,
            productShortDescription,
            productBadge,
            badgeBgColor,
            badgeTextColor,
            hasUpsell
          );
        }
      }
    });
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.close();
    }
  }

  getLoadingHTML() {
    return `
      <div style="display: flex; align-items: center; justify-content: center; min-height: 300px;">
        <svg style="animation: pc-spin 1s linear infinite;" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-opacity="0.25"/>
          <path d="M12 2C6.47715 2 2 6.47715 2 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>
      <style>
        @keyframes pc-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      </style>
    `;
  }

  async open(
    productHandle,
    productUrl,
    productAvailable = true,
    productShortDescription = '',
    productBadge = '',
    badgeBgColor = '',
    badgeTextColor = '',
    hasUpsell = false
  ) {
    if (!this.dialog) return;

    // Show loading state
    this.content.innerHTML = this.getLoadingHTML();

    // Open dialog
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.boundHandleKeydown);

    try {
      // Fetch product data via Section Rendering API
      // Always use canonical /products/ URL to ensure consistent behavior across pages
      const url = productHandle ? `/products/${productHandle}` : productUrl;
      const response = await fetch(`${url}?section_id=product-cards-quick-view-content`);

      if (!response.ok) {
        throw new Error('Failed to fetch product');
      }

      const html = await response.text();

      // If section doesn't exist, fetch the product page and extract info
      if (html.includes('Liquid error') || !html.trim()) {
        await this.loadProductFallback(
          productHandle,
          productUrl,
          productAvailable,
          productShortDescription,
          productBadge,
          badgeBgColor,
          badgeTextColor,
          hasUpsell
        );
      } else {
        this.content.innerHTML = html;
      }
    } catch (error) {
      console.warn('Quick view error:', error);
      await this.loadProductFallback(
        productHandle,
        productUrl,
        productAvailable,
        productShortDescription,
        productBadge,
        badgeBgColor,
        badgeTextColor,
        hasUpsell
      );
    }
  }

  async loadProductFallback(
    productHandle,
    productUrl,
    productAvailable = true,
    productShortDescription = '',
    productBadge = '',
    badgeBgColor,
    badgeTextColor,
    hasUpsell = false
  ) {
    try {
      // Always use canonical /products/ URL for consistent JSON API access
      const apiUrl = productHandle ? `/products/${productHandle}` : productUrl;
      const url = productUrl || `/products/${productHandle}`;
      const response = await fetch(`${apiUrl}.json`);

      if (!response.ok) {
        throw new Error('Failed to fetch product data');
      }

      const data = await response.json();
      const product = data.product;
      const variant = product.variants[0];
      const featuredImage = product.images[0] ? product.images[0].src : '';

      // Use the availability passed from the product card (JSON API doesn't include availability)
      const isProductAvailable = productAvailable;

      // Format price - JSON API returns prices as decimal strings, not cents
      const currency = window.Shopify?.currency?.active || 'GBP';
      const formatMoney = (price) =>
        new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(parseFloat(price));

      this.content.innerHTML = `
        <div style="display: grid; grid-template-columns: 1fr; gap: 24px;">
          <div style="aspect-ratio: 1; background: #f5f5f5; overflow: hidden; padding:16px; position: relative;">
            ${productBadge ? `<span style="position: absolute; top: 12px; left: 12px; padding: 6px 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; background-color: ${badgeBgColor}; color: ${badgeTextColor}; z-index: 1;">${productBadge}</span>` : ''}
            ${
              featuredImage
                ? `<img src="${featuredImage}" alt="${product.title}" loading="lazy" style="width: 100%; height: 100%; object-fit: contain;">`
                : '<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #f0f0f0;"><svg viewBox="0 0 20 20" style="width: 50%; height: 50%; opacity: 0.3;"><path d="M10 0a10 10 0 1 0 10 10A10 10 0 0 0 10 0zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"/></svg></div>'
            }
          </div>
          <div style="display: flex; flex-direction: column; gap: 16px;">
            <h2 style="font-size: 24px; font-weight: 700; margin: 0; color: var(--color-foreground);">${product.title}</h2>
            <div style="font-size: 20px; font-weight: 600; color: var(--color-foreground);">
              ${formatMoney(variant.price)}
              ${
                variant.compare_at_price && variant.compare_at_price > variant.price
                  ? `<span style="font-size: 14px; opacity: 0.6; text-decoration: line-through; margin-left: 8px;">${formatMoney(variant.compare_at_price)}</span>`
                  : ''
              }
            </div>
            ${productShortDescription ? `<div style="font-size: 16px; line-height: 1.6; font-weight: 500; color: var(--color-foreground); opacity: 0.8;">${productShortDescription}</div>` : ''}
            <form method="post" action="/cart/add">
              <input type="hidden" name="id" value="${variant.id}">
              <button type="submit" style="width: 100%; padding: 12px 16px; font-size: 14px; font-weight: 600; text-align: center; border: none; cursor: pointer; background: var(--color-primary-button-background); color: var(--color-primary-button-text);${!isProductAvailable ? ' opacity: 0.5; cursor: not-allowed;' : ''}" ${!isProductAvailable ? 'disabled' : ''}>
                ${isProductAvailable ? 'Add to Cart' : 'Sold Out'}
              </button>
            </form>
            <a href="${url}" style="display: inline-flex; align-items: center; gap: 8px; font-weight: 500; color: var(--color-foreground); font-size: 16px;">View Full Details <svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12" fill="none">
                <path d="M15.7347 6.6483C15.9046 6.47928 16 6.25007 16 6.01108C16 5.77208 15.9046 5.54287 15.7347 5.37385L10.6085 0.275159C10.5249 0.189075 10.4249 0.120411 10.3144 0.0731743C10.2038 0.0259377 10.0849 0.00107397 9.96458 3.40301e-05C9.84426 -0.00100591 9.72493 0.0217989 9.61357 0.0671174C9.5022 0.112436 9.40103 0.179361 9.31595 0.263987C9.23086 0.348613 9.16358 0.449245 9.11802 0.560013C9.07245 0.67078 9.04952 0.789464 9.05057 0.909139C9.05162 1.02881 9.07661 1.14708 9.1241 1.25705C9.1716 1.36701 9.24063 1.46646 9.32718 1.54961L12.9065 5.10977L0.906167 5.10977C0.665836 5.10977 0.435349 5.20473 0.265409 5.37375C0.0954695 5.54278 0 5.77203 0 6.01108C0 6.25012 0.0954695 6.47937 0.265409 6.6484C0.435349 6.81742 0.665836 6.91238 0.906167 6.91238L12.9065 6.91238L9.32718 10.4725C9.16211 10.6425 9.07078 10.8702 9.07284 11.1065C9.07491 11.3428 9.17021 11.5689 9.33822 11.736C9.50623 11.9031 9.73351 11.9979 9.9711 12C10.2087 12.002 10.4376 11.9112 10.6085 11.747L15.7347 6.6483Z" fill="#2B3852"></path>
              </svg></a>
          </div>
        </div>
        <style>
          @media (min-width: 768px) {
            .pc-modal__content > div:first-child {
              grid-template-columns: 1fr 1fr !important;
            }
          }
        </style>
      `;

      // Apply responsive grid on larger screens
      const gridContainer = this.content.querySelector('div');
      if (gridContainer && window.innerWidth >= 768) {
        gridContainer.style.gridTemplateColumns = '1fr 1fr';
      }

      this.#bindCartForm(this.content.querySelector('form'), hasUpsell);
    } catch (error) {
      console.error('Quick view fallback error:', error);
      this.content.innerHTML = `
        <div style="text-align: center; padding: 40px;">
          <p>Unable to load product details.</p>
          <a href="${productUrl || `/products/${productHandle}`}" style="display: inline-block; margin-top: 16px; padding: 12px 16px; font-size: 14px; font-weight: 600; text-align: center; border: none; cursor: pointer; background: var(--color-primary-button-background); color: var(--color-primary-button-text); text-decoration: none;">
            View Product
          </a>
        </div>
      `;
    }
  }

  /**
   * @param {HTMLFormElement | null} form
   * @param {boolean} hasUpsell
   */
  #bindCartForm(form, hasUpsell = false) {
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const btn = form.querySelector('button[type="submit"]');
      if (btn) {
        btn.setAttribute('data-loading', 'true');
        btn.disabled = true;
      }

      try {
        const formData = new FormData(form);

        if (hasUpsell) {
          formData.set(
            'properties[_product_group_id]',
            Date.now().toString(36) + Math.random().toString(36).slice(2)
          );
          formData.set('properties[_is_upsell]', 'false');
        }

        const sectionIds = [];
        document.querySelectorAll('cart-items-component').forEach((el) => {
          const id = el.dataset.sectionId;
          if (id) sectionIds.push(id);
        });
        if (sectionIds.length) {
          formData.append('sections', sectionIds.join(','));
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                data: {
                  sections: data.sections ?? {},
                  source: 'product-cards-quick-view',
                },
              },
            })
          );
          this.close();
        }
      } catch (err) {
        console.error('Add to cart error:', err);
      } finally {
        if (btn) {
          btn.removeAttribute('data-loading');
          btn.disabled = false;
        }
      }
    });
  }

  close() {
    if (!this.dialog) return;

    /** @type {HTMLDialogElement} */ (this.dialog).close();
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.boundHandleKeydown);

    // Reset content
    this.content.innerHTML = this.getLoadingHTML();
  }
}

// Register the custom element
if (!customElements.get('product-cards-quick-view')) {
  customElements.define('product-cards-quick-view', ProductCardsQuickView);
}
